/**
 * We'll load the axios HTTP library which allows us to easily issue requests
 * to our Laravel back-end. This library automatically handles sending the
 * CSRF token as a header based on the value of the "XSRF" token cookie.
 */

import axios from 'axios';

// Create a single axios instance to be used throughout the app
const axiosInstance = axios.create({
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
    },
    withCredentials: true,
    timeout: 30000, // 30 second timeout
});

// Make it available globally for backward compatibility
window.axios = axiosInstance;

// Export for ES6 imports (this ensures consistency)
export default axiosInstance;

// Add interceptor to handle CSRF token mismatches
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    
    failedQueue = [];
};

axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Check for CSRF token mismatch (419 status) and prevent infinite loops
        if (error.response?.status === 419 && !originalRequest._retry && originalRequest.url !== '/sanctum/csrf-cookie') {
            if (isRefreshing) {
                // If we're already refreshing, queue this request
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(() => {
                    return axiosInstance(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }

            // Check if we've already tried too many times
            originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
            if (originalRequest._retryCount > 3) {
                console.error('Max CSRF refresh retries exceeded');
                return Promise.reject(error);
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Get a fresh CSRF token
                console.log('Refreshing CSRF token due to 419 error...');
                await axiosInstance.get('/sanctum/csrf-cookie');
                
                isRefreshing = false;
                processQueue(null);
                
                console.log('CSRF token refreshed successfully, retrying original request');
                
                // Retry the original request
                return axiosInstance(originalRequest);
            } catch (refreshError) {
                isRefreshing = false;
                processQueue(refreshError, null);
                
                console.error('CSRF token refresh failed:', refreshError);
                
                // If refresh fails, check if it's a session/auth issue
                if (refreshError.response?.status === 401 || refreshError.response?.status === 403) {
                    console.warn('Session expired during CSRF refresh');
                    
                    // Dispatch a custom event that the auth store can listen to
                    window.dispatchEvent(new CustomEvent('auth:session-expired', {
                        detail: { error: refreshError }
                    }));
                } else if (refreshError.response?.status === 500) {
                    console.error('Server error during CSRF refresh - may need manual intervention');
                }
                
                return Promise.reject(refreshError);
            }
        }

        // Handle other auth errors
        if (error.response?.status === 401 && originalRequest.url !== '/sanctum/csrf-cookie') {
            console.warn('Unauthorized request detected');
            window.dispatchEvent(new CustomEvent('auth:session-expired', {
                detail: { error }
            }));
        }

        return Promise.reject(error);
    }
);

/**
 * Echo exposes an expressive API for subscribing to channels and listening
 * for events that are broadcast by Laravel. Echo and event broadcasting
 * allows your team to easily build robust real-time web applications.
 */

// import Echo from 'laravel-echo';

// import Pusher from 'pusher-js';
// window.Pusher = Pusher;

// window.Echo = new Echo({
//     broadcaster: 'pusher',
//     key: import.meta.env.VITE_PUSHER_APP_KEY,
//     cluster: import.meta.env.VITE_PUSHER_APP_CLUSTER ?? 'mt1',
//     wsHost: import.meta.env.VITE_PUSHER_HOST ? import.meta.env.VITE_PUSHER_HOST : `ws-${import.meta.env.VITE_PUSHER_APP_CLUSTER}.pusher.com`,
//     wsPort: import.meta.env.VITE_PUSHER_PORT ?? 80,
//     wssPort: import.meta.env.VITE_PUSHER_PORT ?? 443,
//     forceTLS: (import.meta.env.VITE_PUSHER_SCHEME ?? 'https') === 'https',
//     enabledTransports: ['ws', 'wss'],
// });
